import React, { useMemo, useState } from 'react';
import { DayAvailability } from '../../api/types';
import { useUi } from '../../i18n/ui';

export interface DateRange {
  checkIn?: string;
  checkOut?: string;
}

interface Props {
  days: DayAvailability[];
  people: number;
  value: DateRange;
  onChange: (range: DateRange) => void;
  months?: number;
  loading?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (d: Date, n: number) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
/** Indice del giorno della settimana con lunedì = 0. */
const weekdayMon = (d: Date) => (d.getDay() + 6) % 7;

type DayState = 'closed' | 'free' | 'tight';

const AvailabilityCalendar: React.FC<Props> = ({ days, people, value, onChange, months = 2, loading }) => {
  const { tr, months: MONTHS, weekdays: WEEKDAYS } = useUi();
  const byDate = useMemo(() => {
    const m = new Map<string, DayAvailability>();
    days.forEach((d) => m.set(d.date, d));
    return m;
  }, [days]);

  const today = useMemo(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const stateOf = (dayISO: string): DayState => {
    const d = byDate.get(dayISO);
    if (!d || !d.open || d.capacity <= 0) return 'closed';
    return d.availableSeats >= people ? 'free' : 'tight';
  };

  const rangeContainsClosed = (a: string, b: string): boolean => {
    let cur = parseISO(a);
    const end = parseISO(b);
    while (cur <= end) {
      if (stateOf(iso(cur)) === 'closed') return true;
      cur = addDays(cur, 1);
    }
    return false;
  };

  const clickDay = (dayISO: string) => {
    if (stateOf(dayISO) === 'closed') return;
    const { checkIn, checkOut } = value;
    // Nessun inizio, o già un intervallo completo → (ri)parti da qui
    if (!checkIn || checkOut) {
      onChange({ checkIn: dayISO, checkOut: undefined });
      return;
    }
    // Ho l'inizio, sto scegliendo la fine
    if (parseISO(dayISO) < parseISO(checkIn)) {
      onChange({ checkIn: dayISO, checkOut: undefined });
      return;
    }
    if (rangeContainsClosed(checkIn, dayISO)) {
      // Il periodo attraversa un giorno chiuso: riparti da qui
      onChange({ checkIn: dayISO, checkOut: undefined });
      return;
    }
    onChange({ checkIn, checkOut: dayISO });
  };

  const inRange = (dayISO: string): boolean => {
    if (!value.checkIn) return false;
    const end = value.checkOut || value.checkIn;
    const d = parseISO(dayISO);
    return d >= parseISO(value.checkIn) && d <= parseISO(end);
  };

  const renderMonth = (base: Date) => {
    const year = base.getFullYear();
    const month = base.getMonth();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const lead = weekdayMon(first);
    const cells: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

    return (
      <div className="cal__month" key={`${year}-${month}`}>
        <div className="cal__month-name">{MONTHS[month]} {year}</div>
        <div className="cal__grid">
          {WEEKDAYS.map((w) => <div key={w} className="cal__wd">{w}</div>)}
          {cells.map((cell, i) => {
            if (!cell) return <div key={`e${i}`} className="cal__day cal__day--empty" />;
            const dISO = iso(cell);
            const past = cell < today;
            const st = stateOf(dISO);
            const disabled = past || st === 'closed';
            const selected = inRange(dISO);
            const isStart = dISO === value.checkIn;
            const isEnd = dISO === (value.checkOut || value.checkIn) && !!value.checkOut;
            const cls = [
              'cal__day',
              disabled ? 'cal__day--disabled' : `cal__day--${st}`,
              selected ? 'cal__day--sel' : '',
              isStart ? 'cal__day--start' : '',
              isEnd ? 'cal__day--end' : '',
            ].filter(Boolean).join(' ');
            const day = byDate.get(dISO);
            return (
              <button
                type="button"
                key={dISO}
                className={cls}
                disabled={disabled}
                onClick={() => clickDay(dISO)}
                title={day && day.open ? `${day.availableSeats} ${tr('cal.seatsFree')}` : tr('cal.notAvailable')}
              >
                <span className="cal__num">{cell.getDate()}</span>
                {!disabled && <span className="cal__seats">{day ? day.availableSeats : 0}</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const monthsToShow = Array.from({ length: months }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth() + i, 1));
  const canGoPrev = cursor > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <div className="cal">
      <div className="cal__head">
        <button type="button" className="cal__nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} disabled={!canGoPrev} aria-label="Mese precedente">‹</button>
        <span className="cal__legend">
          <i className="cal__dot cal__dot--free" /> {tr('cal.legend.free')}
          <i className="cal__dot cal__dot--tight" /> {tr('cal.legend.tight')}
        </span>
        <button type="button" className="cal__nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Mese successivo">›</button>
      </div>
      {loading ? (
        <p className="cal__loading">{tr('cal.loading')}</p>
      ) : (
        <div className="cal__months">{monthsToShow.map(renderMonth)}</div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
